import type {
    ErrorResponse,
    QueryParams,
    RequestOptions,
    RouteParams,
    SuccessResponse
} from './ajax';
import {
    getJson,
    notifyStandardError,
    postJson,
    resolveRoute,
    responseToErrorMessage,
    stdDelete,
    submitServerRedirect
} from './ajax'
import type {
    DependencyList,
    ReactEventHandler,
    SyntheticEvent} from 'react';
import {
    useCallback,
    useDebugValue,
    useEffect,
    useMemo,
    useState
} from 'react'
import memoize from '../memoize'
import shortid from 'shortid'
import notify from '../notify'
import type {Jsonable, JsonRequest} from './jsonSerializable'
import {Warning} from './errors'
import {resolveValue} from '../util/resolvable'

interface FetchJsonOptions<TData> {
    callback?(data: TData): void
    deps?: any[]
    queryParams?: QueryParams
    fetchOptions?: RequestOptions
}

interface ErrorState {
    status?: number
    /** @deprecated Not support with HTTP/2 */
    statusText?: string
    stack?: string
}

/**
 * @deprecated Use `createGetQuery`
 */
export function useFetchJson<TData=Record<string,any>>(url: string, options: FetchJsonOptions<TData> = {}) {
    const [loading, setLoading] = useState<boolean>(true);
    const [data, setData] = useState<TData|null>(null);
    const [error, setError] = useState<ErrorState|null>(null);

    useEffect(() => {
        const controller = new AbortController();
        getJson<TData>(url, options.queryParams, {
            ...options.fetchOptions,
            signal: controller.signal,
        }).then(res => {
            if (res.ok) {
                if (options.callback) {
                    options.callback(res.data);
                }
                setData(res.data);
                setError(null);
            } else {
                // Sentry?.captureException(`getJson("${appendQueryParams(url,options.queryParams)}") failed")`, {
                //     extra: {
                //         method: 'useFetchJson',
                //         url: url,
                //         queryParams: options.queryParams,
                //         data: res.data,
                //         status: res.status,
                //     }
                // });
                setError({
                    status: res.status,
                    statusText: res.statusText,
                })
            }
        }, err => {
            if(controller.signal.aborted) {
                setError({
                    status: 499,
                    statusText: "Aborted",
                    stack: err?.stack,
                });
            } else {
                // Sentry?.captureException(err, {
                //     extra: {
                //         method: 'useFetchJson',
                //         url: url,
                //         queryParams: options.queryParams,
                //         data: err?.data,
                //         status: err?.status,
                //     }
                // });
                setError({
                    status: 400,
                    statusText: err.message,
                    stack: err.stack,
                });
            }
        }).finally(() => {
            setLoading(false);
        })
        return () => {
            controller.abort("Dependencies changed")
        }
    }, [url, ...(options.deps || [])]);/*TODO: factor in options*/

    return {loading, data, error}
}

export function useArrayCallback<TArgs extends any[]>(fn: (idx: number, ...args: TArgs) => void, deps: DependencyList = []) {
    return useMemo(() => memoize((idx: number) => (...args: TArgs) => fn(idx, ...args), {serialize: (idx: number) => idx}), deps);
}

export function useUuid(id?: string) {
    // TODO: change to useOpaqueIdentifier when it's released
    return useMemo(() => id || shortid(), [id]);
}

/**
 * Sends JSON data to the server and then redirects or refreshes upon success.
 * Doesn't necessarily need to be used in `onSubmit`.
 *
 * @param url URL to submit data to
 * @param data POST data
 */
export function useSubmitRedirectHandler<TReq>(url: string, data?: JsonRequest|Jsonable<TReq>): [ReactEventHandler, boolean] {
    const [saving, setSaving] = useState(false);

    function handler(ev: SyntheticEvent) {
        ev.preventDefault();
        setSaving(true);
        submitServerRedirect(url, data).catch(() => {
            // We're going to redirect on success, so might as well leave the submit buttons disabled on success.
            setSaving(false);
        })
    }

    const callback = useCallback(handler, [url, data, setSaving]);

    useDebugValue({url,data,saving})

    return [callback, saving]
}

export type SaveHandlerOptions<TReq = any, TRes = any> = {
        url?: string
        route?: RouteParams
        data?: TReq | ((ev?: SyntheticEvent<HTMLElement>) => TReq)
        networkError?: (err: Error) => void
        finally?: VoidFn
        /**
         * Do not throw errors. Errors can be customized with .error and .networkError instead.
         */
        noThrow?: boolean
    }
    & XOR<
        | { success?: (res: SuccessResponse<TRes>) => void }
        | { successText?: string | ((res: SuccessResponse<TRes>) => string) }
    >
    & XOR<
        | { error?: (res: ErrorResponse) => void }
        | { errorText?: string | ((res: ErrorResponse) => string) }
    >

export type SaveHandlerType = (ev?: SyntheticEvent<HTMLElement>) => Promise<any>

/**
 * POSTs data to the server. Has default success and error handlers. May be used as a `onSubmit` handler.
 */
export function useSaveHandler<TRes,TReq extends JsonRequest=JsonRequest>(opts: SaveHandlerOptions<TReq,TRes>): [handler:SaveHandlerType,saving:boolean] {
    if(!opts.url && !opts.route) throw new Error("Must set `url` or `route`");
    const [saving, setSaving] = useState(false);
    const url = opts.url ?? resolveRoute(opts.route!)

    const handler: SaveHandlerType = ev => {
        if(ev) ev.preventDefault();
        setSaving(true);
        const data = resolveValue(opts.data, ev)
        if(__DEV__) console.debug('Saving',url,data)

        if (typeof Sentry !== 'undefined') {
            Sentry.addBreadcrumb({
                category: 'call',
                message: 'saveHandler',
                level: Sentry.Severity.Info,
            })
        }

        return postJson<TRes,TReq>(url, data)
            .finally(() => {
                setSaving(false);
                opts.finally?.()
            }).then(res => {
                if(res.ok) {
                    if(opts.success) {
                        return opts.success(res)
                    }
                    notify({
                        className: 'success',
                        text: resolveValue(opts.successText, res) || "Saved",
                    });
                    return res;
                } else {
                    if(opts.error) {
                        return opts.error(res)
                    }
                    notifyStandardError(res, {
                        fallbackText: opts.errorText || "Error saving"
                    })
                    if(!opts.noThrow) {
                        throw new Warning(responseToErrorMessage(res))  // Do not remove. Zones page uses this promise.
                    }
                }
            }, err => {
                if(opts.networkError) {
                    return opts.networkError(err)
                }
                notify({
                    className: 'error',
                    text: "Network error. Try saving again!",
                });
                if(!opts.noThrow) {
                    throw err;
                }
            })
    }

    const callback = useCallback(handler, [opts,setSaving]);
    return [callback, saving]
}

export function useDeleteHandler(url: string, data?: JsonRequest): [ReactEventHandler, boolean] {
    const [deleting, setDeleting] = useState(false);

    function handler(ev: SyntheticEvent) {
        ev.preventDefault();
        setDeleting(true);
        stdDelete(url, data).catch(() => {
            // We're going to redirect on success, so might as well leave the submit buttons disabled.
            setDeleting(false);
        })
    }

    const callback = useCallback(handler, [url, data, setDeleting]);
    return [callback, deleting]
}

const PASSIVE = {passive: true}

